
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useFAQSync } from "@/hooks/useFAQSync";

interface FAQItemData {
  id: string;
  question: string;
  answer: string;
  position: number;
  created_at: string;
  updated_at: string;
  sent_to_frank?: boolean;
  sent_at?: string;
  frank_sync_attempts?: number;
  sync_enabled?: boolean;
  last_sync_attempt?: string;
  sync_error_message?: string;
}

interface UseFAQOperationsProps {
  item: FAQItemData;
  organizationId?: string;
  onUpdate: () => void;
  onDelete: () => void;
}

export const useFAQOperations = ({ item, organizationId, onUpdate, onDelete }: UseFAQOperationsProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { syncFAQToFrank, getAssociationId } = useFAQSync();

  const handleSave = async (question: string, answer: string) => {
    if (!question.trim() || !answer.trim()) {
      toast.error("Please fill in both question and answer");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('faq_items')
        .update({
          question: question.trim(),
          answer: answer.trim(),
          updated_at: new Date().toISOString(),
          sent_to_frank: false,
          frank_sync_attempts: 0
        })
        .eq('id', item.id);

      if (error) throw error;

      toast.success("FAQ item updated successfully");

      if (organizationId) {
        const associationId = await getAssociationId(organizationId);
        if (associationId) {
          await syncFAQToFrank({
            faq_id: item.id,
            question: question.trim(),
            answer: answer.trim(),
            association_id: associationId,
            action: 'update'
          }, organizationId);
        }
      }

      setIsEditing(false);
      onUpdate();
    } catch (error: any) {
      console.error('Error updating FAQ:', error);
      toast.error('Failed to update FAQ item');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this FAQ item?")) {
      return;
    }

    setIsLoading(true);
    try {
      if (organizationId) {
        const associationId = await getAssociationId(organizationId);
        if (associationId) {
          await syncFAQToFrank({
            faq_id: item.id,
            question: item.question,
            answer: item.answer,
            association_id: associationId,
            action: 'delete'
          }, organizationId);
        }
      }

      const { error } = await supabase
        .from('faq_items')
        .delete()
        .eq('id', item.id);

      if (error) throw error;

      toast.success("FAQ item deleted successfully");
      onDelete();
    } catch (error: any) {
      console.error('Error deleting FAQ:', error);
      toast.error('Failed to delete FAQ item');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  return {
    isEditing,
    isLoading,
    setIsEditing,
    handleSave,
    handleDelete,
    handleCancel
  };
};
